// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {UrlFiltersType, useEffect} from '@supermove/hooks';
import {OrganizationModel} from '@supermove/models';
import {colors} from '@supermove/styles';

// Shared
import Line from '@shared/design/components/Line';
import Tabs from '@shared/design/components/Tabs';
import ProjectDetailsWidgetSection from '@shared/modules/Project/components/widgets/components/ProjectDetailsWidgetSection';
import {ProjectDetailsContextProvider} from '@shared/modules/Project/components/widgets/hooks/useProjectDetailsContext';
import {NavigationSectionType} from '@shared/modules/Project/components/widgets/types/NavigationSection';
import ProjectBlockKind, {
  ProjectBlockKindType,
} from '@shared/modules/Project/enums/ProjectBlockKind';

const TabsContainer = Styled.View`
  background-color: ${colors.white};
`;

export type ProjectDetailsFiltersType = {
  projectUuid: string;
  block: ProjectBlockKindType;
};
export type ProjectDetailsUrlFiltersType = UrlFiltersType<ProjectDetailsFiltersType>;

export const getNavigationSections = ({
  isEnabledProjectPageMarketingBlock,
}: {
  isEnabledProjectPageMarketingBlock: boolean;
}) => {
  // This will move to the backend, as a property of project,
  // once the project page becomes configurable.
  return [
    {
      name: 'Details',
      blocks: [
        ProjectBlockKind.PROJECT_OVERVIEW,
        ProjectBlockKind.CLIENTS,
        ...(isEnabledProjectPageMarketingBlock
          ? [
              ProjectBlockKind.ASSIGNED_STAFF,
              ProjectBlockKind.LEAD_INFORMATION,
              ProjectBlockKind.REFERRAL,
            ]
          : [ProjectBlockKind.INTERNAL]),
      ],
    },
  ];
};

const useBlockParam = ({
  block,
  handleUpdateBlock,
  navigationSections,
}: {
  block: ProjectBlockKindType;
  handleUpdateBlock: (block: ProjectBlockKindType) => void;
  navigationSections: {name: string; blocks: ProjectBlockKindType[]}[];
}) => {
  useEffect(() => {
    if (!block) {
      const firstBlock = _.get(navigationSections, '0.blocks.0');
      return handleUpdateBlock(firstBlock);
    }
  }, [block]);
};

const getTabs = ({navigationSections}: {navigationSections: NavigationSectionType[]}) => {
  return navigationSections.map((section) => {
    return {
      key: _.lowerCase(section.name.replace(/\s/g, '_')),
      label: section.name,
      section,
    };
  });
};

const ProjectDetailsWidgetContent = ({
  projectUuid,
  block,
  handleUpdateBlock,
  navigationSections,
}: {
  projectUuid: string;
  block: ProjectBlockKindType;
  handleUpdateBlock: (block: ProjectBlockKindType) => void;
  navigationSections: NavigationSectionType[];
}) => {
  const tabs = getTabs({navigationSections});

  return (
    <React.Fragment>
      {tabs.length > 1 && (
        <React.Fragment>
          <TabsContainer>
            <Space height={16} />
            <Tabs
              tabs={tabs}
              handlePressTab={({section}) => handleUpdateBlock(section.blocks[0])}
              activeTabIndex={
                block
                  ? tabs.findIndex((tab) =>
                      _.some(tab.section.blocks, (sectionBlock) => sectionBlock === block),
                    )
                  : 0
              }
              style={{paddingLeft: 16}}
              tabStyle={{paddingLeft: 4, paddingRight: 4}}
            />
          </TabsContainer>
          <Line />
        </React.Fragment>
      )}
      <ProjectDetailsWidgetSection
        projectUuid={projectUuid}
        block={block}
        handleUpdateBlock={handleUpdateBlock}
        projectNavigationSections={navigationSections}
      />
    </React.Fragment>
  );
};

const ProjectDetailsWidget = ({
  projectUuid,
  block,
  handleUpdateBlock,
  organization,
}: {
  projectUuid: string;
  block: ProjectBlockKindType;
  handleUpdateBlock: (block: string) => void;
  organization?: OrganizationModel;
}) => {
  const isEnabledProjectPageMarketingBlock =
    organization?.features?.isEnabledProjectPageMarketingBlock || false;
  const navigationSections = getNavigationSections({isEnabledProjectPageMarketingBlock});
  useBlockParam({block, handleUpdateBlock, navigationSections});

  return (
    <ProjectDetailsContextProvider>
      <ProjectDetailsWidgetContent
        projectUuid={projectUuid}
        block={block}
        handleUpdateBlock={handleUpdateBlock}
        navigationSections={navigationSections}
      />
    </ProjectDetailsContextProvider>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
ProjectDetailsWidget.fragment = gql`
  fragment ProjectDetailsWidget on Organization {
    id
    features {
      isEnabledProjectPageMarketingBlock: isEnabled(feature: "PROJECT_PAGE_MARKETING_BLOCK")
    }
  }
`;

export default ProjectDetailsWidget;
